import { useState } from "react";
import axios, { AxiosError } from "axios";
import { Table } from "react-bootstrap";
import { Variant } from "react-bootstrap/esm/types";
import { DateTime } from "luxon";

import { formatLedgerAmount } from "../../helpers/formatLedgerAmount";
import ErrorPage from "../ErrorPage";
import PageLoader from "../PageLoader";
import StatusBadge from "../StatusBadge";
import {
  LedgerActivityItem,
  LedgerActivityKind,
  LedgerActivityResult,
  LedgerActivityStatus,
} from "../../types/ledgerActivity";
import styles from "./index.module.scss";

type Props = {
  data?: LedgerActivityResult;
  error?: Error | null;
  isLoading: boolean;
  idleMessage?: string;
};

const statusEmphasis: Record<LedgerActivityStatus, Variant> = {
  PENDING: "warning",
  SETTLED: "success",
  REVERSED: "secondary",
  EXPIRED: "danger",
};

const kindLabel: Record<LedgerActivityKind, string> = {
  LEDGER_ENTRY: "Ledger entry",
  HOLD: "Hold",
};

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const dt = DateTime.fromISO(iso, { zone: "utc" });
  return dt.isValid ? dt.toFormat("dd MMM yyyy HH:mm") : iso;
};

const CopyCell = ({ value }: { value: string | null }) => {
  const [copied, setCopied] = useState(false);

  if (!value) return <span>—</span>;

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className={styles.copyCell}>
      <span className={styles.copyValue}>{value}</span>
      <button className={styles.copyBtn} onClick={handleCopy} title="Copy">
        <span className="material-icons" style={{ fontSize: 14 }}>
          {copied ? "check" : "content_copy"}
        </span>
      </button>
    </div>
  );
};

const COLUMN_COUNT = 9;

const TableHead = () => (
  <thead>
    <tr>
      <th>Kind</th>
      <th>ID</th>
      <th>Occurred at</th>
      <th>Amount</th>
      <th>Category / type</th>
      <th>Status</th>
      <th>Correlation ID</th>
      <th>External ref</th>
      <th>Account ID</th>
    </tr>
  </thead>
);

const TableRow = ({ item }: { item: LedgerActivityItem }) => (
  <tr>
    <td>{kindLabel[item.kind] ?? item.kind}</td>
    <td>
      <CopyCell value={item.id} />
    </td>
    <td>{formatDate(item.occurredAt)}</td>
    <td>{formatLedgerAmount(item.amount, item.currency)}</td>
    <td>{item.categoryOrType || "—"}</td>
    <td>
      {item.status ? (
        <StatusBadge
          status={item.status}
          emphasis={statusEmphasis[item.status] ?? "secondary"}
        />
      ) : (
        "—"
      )}
    </td>
    <td>
      <CopyCell value={item.correlationId} />
    </td>
    <td>
      <CopyCell value={item.externalRef} />
    </td>
    <td>
      <CopyCell value={item.accountId} />
    </td>
  </tr>
);

const TableBody = ({ items }: { items: LedgerActivityItem[] }) => (
  <tbody>
    {items.length > 0 ? (
      items.map((item) => (
        <TableRow key={`${item.kind}-${item.id}`} item={item} />
      ))
    ) : (
      <tr>
        <td colSpan={COLUMN_COUNT} className={styles.emptyTable}>
          Oops, no results were found.
        </td>
      </tr>
    )}
  </tbody>
);

const LedgerActivityTable = ({
  data,
  error,
  isLoading,
  idleMessage,
}: Props) => {
  if (isLoading)
    return (
      <PageLoader isLoading={isLoading} content="Loading ledger activity..." />
    );

  if (idleMessage && !data && !error) {
    return (
      <div className={styles.table}>
        <Table hover bordered responsive="md">
          <TableHead />
          <tbody>
            <tr>
              <td colSpan={COLUMN_COUNT} className={styles.emptyTable}>
                {idleMessage}
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
    );
  }

  if (error || !data) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{
        errorCode: string;
        errorMessage: string;
      }>;
      return (
        <ErrorPage
          title={axiosError.response?.data?.errorCode}
          message={axiosError.response?.data?.errorMessage}
        />
      );
    }
    return <ErrorPage />;
  }

  return (
    <div className={styles.table}>
      <Table hover bordered responsive="md">
        <TableHead />
        <TableBody items={data.items} />
      </Table>
    </div>
  );
};

export default LedgerActivityTable;
